import type { MentionRow } from './generator';
import { allRows } from './rows';

/**
 * Tune sentiment — a person's correction of the classifier, applied everywhere.
 *
 * The Competitors frame's `Tune sentiment` modal (2037:7616) lets a reader say
 * "this post was detected Negative but is actually Neutral". Settled with the
 * user 2026-09-18: a saved correction **replaces the detected sentiment for the
 * whole app for the rest of the session** — every sentiment chart, KPI, table
 * and the Sentiments chip itself read the corrected value. A reload starts from
 * the classifier again.
 *
 * It is applied once, where rows enter the app (`DashboardProvider`'s date
 * slice), rather than in each view. That is what makes "everywhere" true by
 * construction: no module can forget to honour a correction, because none of
 * them ever sees an uncorrected row.
 */

/**
 * A row's stable identity: its position in the generated corpus.
 *
 * Kept in a `WeakMap` beside the corpus rather than as an `id` field on
 * `MentionRow`, so the corpus type and its generator are untouched. The cost is
 * that a *copy* of a row has no identity until it is given one — which is
 * exactly what `withSentiment` does, and it is the only place rows are copied.
 */
const identity = new WeakMap<MentionRow, number>();
allRows.forEach((row, index) => identity.set(row, index));

export type RowId = number;

export function rowId(row: MentionRow): RowId {
  const id = identity.get(row);
  if (id === undefined) {
    /*
     * Loud rather than a fallback. A row with no identity came from somewhere
     * other than the corpus or `withSentiment`, and a correction keyed on a
     * made-up id would silently attach to the wrong post.
     */
    throw new Error('sentimentOverrides: row has no identity — it was copied outside withSentiment.');
  }
  return id;
}

/**
 * What the classifier said, whatever has been corrected since. Read straight
 * off the untouched corpus, which is what makes "detected" mean detected.
 */
export function detectedSentiment(row: MentionRow): string {
  return allRows[rowId(row)].sentiment;
}

/** Corrections by row id. A correction may equal the detected value — a confirmation. */
export type SentimentOverrides = Readonly<Record<RowId, string>>;

function withSentiment(row: MentionRow, sentiment: string): MentionRow {
  const copy = { ...row, sentiment };
  identity.set(copy, rowId(row));
  return copy;
}

/**
 * The rows with every correction applied. Rows without one are returned as the
 * same objects, so an empty override set costs one lookup per row and changes
 * nothing downstream — including the memo keys that compare arrays by identity.
 */
export function applySentimentOverrides(
  rows: readonly MentionRow[],
  overrides: SentimentOverrides,
): readonly MentionRow[] {
  if (Object.keys(overrides).length === 0) return rows;
  return rows.map((row) => {
    const next = overrides[rowId(row)];
    return next === undefined || next === row.sentiment ? row : withSentiment(row, next);
  });
}
